import { ChargerDtoV2, ConnectorV2 } from '../component/charger/dto/charger.dto';
import { USER_ROLE } from '../hooks/useAuth';
import { GetFeaturesResponse } from '../stores/types/userPreferences.interface';
import { ConnectorStatus } from '../types/connector-status.enum';
import { API_KEYS, Api } from './base.api';

export const CHARGER_STATUS = {
  AVAILABLE: 'available',
  CHARGING: 'charging',
  UHAVAILABLE: 'Unavailable',
};

const isAnyConnectorAvailable = (ports: ConnectorV2[], maintenanceFlag: boolean) => {
  return (
    ports?.some((port) =>
      maintenanceFlag
        ? port.statusWithoutMaintenance?.toLowerCase() === ConnectorStatus.AVAILABLE.toLowerCase()
        : port.status.toLowerCase() === ConnectorStatus.AVAILABLE.toLowerCase(),
    ) || false
  );
};

const isSingleConnector = (ports: ConnectorV2[]) => {
  return ports?.length === 1 || false;
};

const resolveChargerType = (type: string): string => {
  const normalized = type?.toUpperCase().replace(/\s/g, '_') || '';
  switch (normalized) {
    case 'LEVEL_1':
    case 'LEVEL_2':
      return 'AC';
    case 'LEVEL_3':
    case 'DC_FAST':
      return 'DC';
    default:
      return 'AC';
  }
};

export interface FetchChargerRequest {
  chargerName: string;
  isLoggedIn: boolean;
}

export interface FetchChargerByIdRequest {
  chargerId: string;
  isLoggedIn: boolean;
}

export interface ChangeChargerAvailabilityRequest {
  chargerId: string;
  type: 'Operative' | 'Inoperative';
}

export const chargersApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchChargerDetail: builder.query<ChargerDtoV2, FetchChargerRequest>({
      query: (params: FetchChargerRequest) => ({
        url: `/core/v1/${API_KEYS.CHARGERS}?filter_eq[name]=${params.chargerName}`,
        method: 'GET',
        params: {
          role: USER_ROLE.DRIVER,
        },
        noAuthNeeded: !params.isLoggedIn,
      }),
      providesTags: (result, error, arg) => [{ type: API_KEYS.CHARGERS, id: 'LIST' }],
      transformResponse: (
        response: {
          totalCount: number;
          entities: ChargerDtoV2[];
        },
        meta: any,
      ) => {
        // Length check is needed as for non-exisitent chargers we still send fields (isAnyConnectorAvailable & isSingleConnector)
        // Which breaks UI and doesn't redirect to 404 Charger not found page
        const result =
          response?.entities?.length > 0
            ? ({
                ...response.entities[0],
                formattedType: resolveChargerType(response.entities[0].type),
                isAnyConnectorAvailable: isAnyConnectorAvailable(
                  response?.entities[0]?.ports,
                  response.entities[0].maintenanceFlag,
                ),
                isSingleConnector: isSingleConnector(response?.entities[0]?.ports),
                ports: response.entities[0].ports.map((port) => {
                  /**
                   * In our code, we consistently use the actual status of the port.
                   * Therefore, when maintenanceFlag is set to true, we need to override the
                   * fake status with statusWithoutMaintenance.
                   */
                  return {
                    ...port,
                    status: response.entities[0].maintenanceFlag
                      ? port.statusWithoutMaintenance
                      : port.status,
                  };
                }),
                // In order for FE to access custom header from response:
                // Access-Control-Expose-Headers with custom header value should be present in the response
                siteHostSignUpWebLink: meta?.response?.headers.get('Site-Host-Sign-Up-Web-Link'),
                siteHostSignUpChannelPartner: meta?.response?.headers.get(
                  'Site-Host-Sign-Up-Channel-Partner',
                ),
                // To determine if charger is owned by Test company FE needs to access custon header from response
                testCompany: meta?.response?.headers.get('Test-Company'),
              } as ChargerDtoV2)
            : null;
        return result;
      },
    }),
    fetchChargerFeatures: builder.query<GetFeaturesResponse, FetchChargerRequest>({
      query: (params: FetchChargerRequest) => ({
        url: `/core/v1/${API_KEYS.CHARGERS}/${params.chargerName}/features`,
        method: 'GET',
        noAuthNeeded: !params.isLoggedIn,
      }),
      providesTags: (result, error, arg) => [{ type: API_KEYS.FEATURES, id: 'FEATURES' }],
    }),
    fetchChargerNameById: builder.query<string, FetchChargerByIdRequest>({
      query: (params: FetchChargerByIdRequest) => ({
        url: `/core/v1/${API_KEYS.CHARGERS}/${params.chargerId}`,
        method: 'GET',
        params: {
          role: USER_ROLE.DRIVER,
        },
        noAuthNeeded: !params.isLoggedIn,
      }),
      providesTags: (result, error, arg) => {
        return [{ type: API_KEYS.CHARGERS, id: arg.chargerId }];
      },
      transformResponse: (response: ChargerDtoV2) => {
        return response?.name || '';
      },
    }),
    changeChargerAvailability: builder.mutation<void, ChangeChargerAvailabilityRequest>({
      query: ({ chargerId, type }) => ({
        url: `/internal/core/v2/chargers/${chargerId}/availability?type=${type}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useFetchChargerDetailQuery,
  useLazyFetchChargerDetailQuery,
  useFetchChargerNameByIdQuery,
  useLazyFetchChargerNameByIdQuery,
  useFetchChargerFeaturesQuery,
  useLazyFetchChargerFeaturesQuery,
  useChangeChargerAvailabilityMutation,
} = chargersApi;
